import { z } from "zod";
import { requireSpanishWhenEnglishPresent } from "@portal/common/bilingualSchema";
import { requireValidityOrder } from "@portal/common/validitySchema";

// Mensaje literal de SPEC-210 §2/§8 (`COST_INVALID`): es el contrato de UX,
// no una redacción libre — el servidor devuelve el mismo código si el
// cliente lo saltea (mismo criterio que `GOAL_INVALID_MESSAGE` en
// grand-prize/grandPrizeSchemas.ts).
export const COST_INVALID_MESSAGE =
  "El costo en puntos debe ser mayor que cero";

// Hallazgo (revisión en vivo 2026-09-23): `reward.service.ts` (`isBilingual`)
// exige español cuando el objeto trae inglés — `descriptionEs` vacío con
// `descriptionEn` lleno viajaba igual y el servidor lo rechazaba con
// `INVALID_REQUEST` sin campo (antes de este fix). El mensaje repite el que
// arma ahora `reward.service.ts` vía `fieldRejection`, para que la validación
// de cliente y el rechazo del servidor lean igual.
export const DESCRIPTION_ES_REQUIRED_MESSAGE =
  "La descripción en español es obligatoria cuando hay texto en inglés";

export const PLACES_REQUIRED_MESSAGE =
  "Asocia al menos un comercio aliado o un taller donde se pueda consumir";

const rewardFormObjectSchema = z.object({
  nameEs: z.string().trim().min(1, "El nombre de la recompensa es obligatorio"),
  nameEn: z.string(),
  descriptionEs: z.string(),
  descriptionEn: z.string(),
  imageUrl: z.string(),
  // PLAN-007 Fase 4d: id del archivo ya subido y confirmado
  // (`uploadImage`/`FileUploadChip`), con prioridad sobre `imageUrl` en el
  // servidor (RewardInput.imageFileId). Vacío cuando el operador no tocó la
  // imagen en esta sesión del formulario.
  imageFileId: z.string(),
  pointsCost: z
    .number()
    .int(COST_INVALID_MESSAGE)
    .positive(COST_INVALID_MESSAGE),
  categoryId: z.string().trim().min(1, "La categoría es obligatoria"),
  merchantIds: z.array(z.string()),
  workshopIds: z.array(z.string()),
  validFrom: z.string(),
  validUntil: z.string(),
  active: z.boolean(),
});

export const rewardFormSchema = rewardFormObjectSchema.superRefine(
  (values, ctx) => {
    requireSpanishWhenEnglishPresent(values, ctx, [
      {
        esField: "descriptionEs",
        enField: "descriptionEn",
        message: DESCRIPTION_ES_REQUIRED_MESSAGE,
      },
    ]);
    requireValidityOrder(values, ctx);
    // QA-MTZ-002 ronda 3 (#6): sin lugar asociado la recompensa no se puede
    // consumir en ninguna parte; la API también lo rechaza.
    if (!values.merchantIds.length && !values.workshopIds.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["merchantIds"],
        message: PLACES_REQUIRED_MESSAGE,
      });
    }
  },
);
export type RewardFormValues = z.infer<typeof rewardFormObjectSchema>;

// Mismo criterio que `CATALOG_ENTRY_FORM_FIELDS`/`MEMBER_FORM_FIELDS`: la
// lista de claves que `RewardEditor` realmente renderiza, para
// `applyApiFieldErrors`.
export const REWARD_FORM_FIELDS = Object.keys(
  rewardFormObjectSchema.shape,
) as readonly (keyof RewardFormValues)[];
